import { useEffect, useRef, useState } from 'react'
import { onPending } from '../lib/progress'

type State = 'idle' | 'going' | 'done'

/**
 * A thin bar along the top of the window while something you clicked is loading.
 *
 * It waits 150ms before showing, so a quick answer shows nothing at all; then it runs
 * towards 85% on a long ease (it cannot know how long is left, so it slows as it goes),
 * and when the last request finishes it runs to the end and fades. Back to idle happens
 * out of sight, with no transition, so it never visibly shrinks. The same idea as the bar
 * on GitHub or YouTube: the page is plainly working, without a spinner in the way.
 * Requests on timers do not move it (lib/progress.ts).
 */
export default function ProgressBar() {
  const [state, setStateRaw] = useState<State>('idle')
  const now = useRef<State>('idle')
  const wait = useRef<number>()
  const hide = useRef<number>()
  const set = (s: State) => { now.current = s; setStateRaw(s) }

  useEffect(() => {
    const off = onPending((n) => {
      window.clearTimeout(hide.current)
      if (n > 0) {
        if (now.current === 'going' || wait.current) return
        if (now.current === 'done') set('idle')
        wait.current = window.setTimeout(() => { wait.current = undefined; set('going') }, 150)
      } else {
        window.clearTimeout(wait.current); wait.current = undefined
        if (now.current !== 'going') return
        set('done')
        hide.current = window.setTimeout(() => set('idle'), 500)
      }
    })
    return () => { off(); window.clearTimeout(wait.current); window.clearTimeout(hide.current) }
  }, [])

  return (
    <div aria-hidden="true" className="pointer-events-none fixed inset-x-0 z-[60] h-0.5" style={{ top: 'env(safe-area-inset-top, 0px)' }}>
      <div data-state={state} className="lq-progress h-full rounded-r-full bg-brand-600 shadow-[0_0_6px_rgba(45,104,220,0.5)]"
        style={{ width: state === 'idle' ? '0%' : state === 'going' ? '85%' : '100%', opacity: state === 'going' ? 1 : 0 }} />
    </div>
  )
}
